import { Badge } from "../../client/components/ui/badge";
import { Card, CardContent } from "../../client/components/ui/card";
import { CardSectionHeader } from "../../client/components/ui/card-section-header";
import useTranslation from "../../client/hooks/useTranslation";
import { formatEur } from "./formatEur";

interface PayoutRow {
  id: string;
  createdAt: Date | string;
  amount: number;
  status: string;
  paidAt: Date | string | null;
}

interface PayoutsSectionProps {
  payouts: PayoutRow[];
  isDark: boolean;
}

export default function PayoutsSection({ payouts, isDark }: PayoutsSectionProps) {
  const t = useTranslation();

  const statusLabel = (status: string) => {
    switch (status) {
      case "requested":
        return t.affiliate.status.requested;
      case "paid":
        return t.affiliate.status.paid;
      case "rejected":
        return t.affiliate.status.rejected;
      default:
        return status;
    }
  };

  const statusVariant = (
    status: string,
  ): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case "paid":
        return "default";
      case "rejected":
        return "destructive";
      default:
        return "outline";
    }
  };

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.affiliate.payout.historyTitle}
        isDark={isDark}
        compact
      />
      <CardContent className="px-4 pb-4 pt-3 flex-1">
        {payouts.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            {t.affiliate.payout.historyEmpty}
          </p>
        ) : (
          <ul className="divide-y divide-border/50">
            {payouts.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between gap-3 py-2.5"
              >
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  {new Date(p.paidAt ?? p.createdAt).toLocaleDateString()}
                </span>
                <span className="font-mono font-semibold tabular-nums text-sm">
                  {formatEur(p.amount)}
                </span>
                <Badge variant={statusVariant(p.status)}>
                  {statusLabel(p.status)}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
